/**
 * Demo data fixture
 *
 * When the demo user signs in, DataServiceCore swaps its in-memory `db` for
 * a fresh copy of this fixture and disables Supabase + localStorage writes.
 * Nothing here ever reaches the production database, and every reload
 * starts from this curated state, so the demo is invisible to admins,
 * leaks nowhere, and always looks the same.
 *
 * To change the demo email pattern, edit `DEMO_USER_EMAILS` below.
 */

import type {
  Database, Company, Project, ContractItem, InterimApplication,
  User,
} from '../../types/database';
import type { Activity } from '../../types/activity';
import type { Task } from '../../types/tasks';

// Any Supabase user whose email matches one of these is treated as the
// demo account: DataServiceCore swaps in the fixture below and gates off
// Supabase, Realtime and localStorage writes, so the session leaves no
// trace on the device or the database.
//
// Matching is on the authenticated user's email, not on how they signed
// in, so one address here covers Continue with Apple, Continue with
// Google and the email form alike.
//
// demo@contractgroup.co.uk is listed on purpose, and it is also the
// sign-in handed to App Review. That is the intent: reviewers and testers
// get the same curated workspace every time, rather than whatever the live
// business happens to hold that week.
//
// Two consequences worth remembering. Shipping a change to this list moves
// the ground under any App Review already in flight, because the Apple
// shells load the production web app. And the isolation is client-side:
// the account's real rows still exist in Postgres, they are simply no
// longer reachable through the app. For a server-side guarantee, strip the
// account's workspace memberships so RLS denies it directly.
export const DEMO_USER_EMAILS: ReadonlyArray<string> = [
  'demo@contractgroup.co.uk',
];

export function isDemoUser(user: { email?: string | null } | null | undefined): boolean {
  if (!user?.email) return false;
  const email = user.email.toLowerCase().trim();
  return DEMO_USER_EMAILS.some(demo => demo.toLowerCase() === email);
}

const DEMO_COMPANY_ID = 'demo-co-001';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ms = 86_400_000; // one day in ms
const isoDaysAgo   = (n: number) => new Date(Date.now() - n * ms).toISOString();
const isoDaysAhead = (n: number) => new Date(Date.now() + n * ms).toISOString();
const dateOnly     = (iso: string) => iso.slice(0, 10);

// ─── Demo company ─────────────────────────────────────────────────────────────

function buildCompany(companyId: string): Company {
  return {
    id: companyId,
    name: 'Smith Build Ltd',
    type: 'client',
    email: 'demo@contractgroup.co.uk',
    phone: '0161 240 6996',
    addressLine1: 'Unit 14, Trafford Park Industrial Estate',
    town: 'Manchester',
    county: 'Greater Manchester',
    postcode: 'M17 1AB',
    status: 'active',
    billing_tier: 'professional',
    billing_frequency: 'monthly',
    contract_term: 'rolling',
    primaryContact: {
      name: 'John Smith',
      email: 'demo@contractgroup.co.uk',
      phone: '0161 240 6996',
      position: 'Commercial Director',
    },
    createdAt: isoDaysAgo(420),
    updatedAt: isoDaysAgo(2),
  };
}

// ─── Demo projects ────────────────────────────────────────────────────────────

interface ProjectSeed {
  id: string;
  name: string;
  number: string;
  contractor: string;
  stage: Project['stage'];
  contractValue: number;
  certified: number;
  paid: number;
  startedDaysAgo: number;
  postcode: string;
  town: string;
  lat: number;
  lng: number;
}

const PROJECT_SEEDS: ProjectSeed[] = [
  {
    id: 'demo-proj-001',
    name: 'Westwood Mews',
    number: '0018',
    contractor: 'Brookford Construction',
    stage: 'on_site',
    contractValue: 980_000,
    certified: 540_000,
    paid: 495_000,
    startedDaysAgo: 220,
    postcode: 'SW18 4LR',
    town: 'Wandsworth',
    lat: 51.4569,
    lng: -0.1939,
  },
  {
    id: 'demo-proj-002',
    name: 'Aldgate Square Phase II',
    number: '0022',
    contractor: 'Holden & Marsh',
    stage: 'on_site',
    contractValue: 2_400_000,
    certified: 1_010_000,
    paid: 960_000,
    startedDaysAgo: 165,
    postcode: 'E1 7AA',
    town: 'Aldgate',
    lat: 51.5142,
    lng: -0.0731,
  },
  {
    id: 'demo-proj-003',
    name: 'Battersea Wharf Block C',
    number: '0034',
    contractor: 'Linbrook Build',
    stage: 'internal_review',
    contractValue: 1_780_000,
    certified: 0,
    paid: 0,
    startedDaysAgo: 14,
    postcode: 'SW8 5BN',
    town: 'Battersea',
    lat: 51.4775,
    lng: -0.1424,
  },
  {
    id: 'demo-proj-004',
    name: 'Hampstead Garden',
    number: '0037',
    contractor: 'Pemberton Heritage Build',
    stage: 'internal_review',
    contractValue: 420_000,
    certified: 0,
    paid: 0,
    startedDaysAgo: 6,
    postcode: 'NW3 7HX',
    town: 'Hampstead',
    lat: 51.5550,
    lng: -0.1762,
  },
  {
    id: 'demo-proj-005',
    name: 'Kingsland Yard',
    number: '0041',
    contractor: 'Thornhill Group',
    stage: 'tendering',
    contractValue: 1_650_000,
    certified: 0,
    paid: 0,
    startedDaysAgo: 32,
    postcode: 'E8 2AA',
    town: 'Dalston',
    lat: 51.5476,
    lng: -0.0762,
  },
  {
    id: 'demo-proj-006',
    name: 'Marylebone Studios',
    number: '0011',
    contractor: 'Brookford Construction',
    stage: 'final_account',
    contractValue: 290_000,
    certified: 282_000,
    paid: 274_000,
    startedDaysAgo: 380,
    postcode: 'W1U 5BG',
    town: 'Marylebone',
    lat: 51.5215,
    lng: -0.1561,
  },
  {
    id: 'demo-proj-007',
    name: 'Greenwich Quay Block A',
    number: '0044',
    contractor: 'Holden & Marsh',
    stage: 'pre_start',
    contractValue: 1_950_000,
    certified: 0,
    paid: 0,
    startedDaysAgo: 9,
    postcode: 'SE10 9NF',
    town: 'Greenwich',
    lat: 51.4934,
    lng: 0.0098,
  },
];

// Derive tender dates relative to today based on stage. Real construction
// timelines run Issue → Due → Submission → award (contract signing) → start
// on site. The fixture mirrors that chain so tender_in_progress sits before
// the due date, client_review sits just after submission, etc.
function tenderDatesFor(stage: Project['stage'], startedDaysAgo: number) {
  switch (stage) {
    case 'itt_received':
      return {};
    case 'tendering':
      // Tender is out, returns not due yet
      return {
        tenderIssueDate:    dateOnly(isoDaysAgo(startedDaysAgo)),
        tenderDueDate:      dateOnly(isoDaysAhead(14)),
      };
    case 'internal_review':
      // Returns are in, waiting on client decision
      return {
        tenderIssueDate:      dateOnly(isoDaysAgo(startedDaysAgo + 28)),
        tenderDueDate:        dateOnly(isoDaysAgo(2)),
        tenderSubmissionDate: dateOnly(isoDaysAgo(2)),
      };
    case 'cleared_for_submission':
    case 'tender_submitted':
      return {
        tenderIssueDate:      dateOnly(isoDaysAgo(startedDaysAgo + 21)),
        tenderDueDate:        dateOnly(isoDaysAgo(startedDaysAgo)),
        tenderSubmissionDate: dateOnly(isoDaysAgo(startedDaysAgo)),
      };
    case 'pre_start':
    case 'on_site':
    case 'final_account':
    case 'defects_liability':
    case 'closed':
      // Tender phase is in the past
      return {
        tenderIssueDate:      dateOnly(isoDaysAgo(startedDaysAgo + 60)),
        tenderDueDate:        dateOnly(isoDaysAgo(startedDaysAgo + 30)),
        tenderSubmissionDate: dateOnly(isoDaysAgo(startedDaysAgo + 28)),
      };
    default:
      return {};
  }
}

function buildProject(seed: ProjectSeed, companyId: string, userId: string): Project {
  const created = isoDaysAgo(seed.startedDaysAgo);
  const start = isoDaysAgo(Math.max(seed.startedDaysAgo - 14, 0));
  const endIso = isoDaysAhead(180);
  // Construction-style dates: PC 14 days before contractual end, defects
  // liability ends 12 months after PC.
  const practicalCompletion = isoDaysAhead(180 - 14);
  const defectsLiabilityEnd = isoDaysAhead(180 - 14 + 365);
  const tenderDates = tenderDatesFor(seed.stage, seed.startedDaysAgo);
  return {
    id: seed.id,
    name: seed.name,
    projectNumber: seed.number,
    clientId: companyId,
    mainContractor: seed.contractor,
    contractValue: seed.contractValue,
    stage: seed.stage,
    serviceType: 'full-qs',
    startDate: dateOnly(start),
    endDate: dateOnly(endIso),
    practicalCompletionDate: dateOnly(practicalCompletion),
    defectsLiabilityEndDate: dateOnly(defectsLiabilityEnd),
    ...tenderDates,
    originalContractValue: seed.contractValue,
    revisedContractValue: seed.contractValue,
    certifiedToDate: seed.certified,
    paidToDate: seed.paid,
    contractType: 'JCT',
    contractNumber: `CG-${seed.number}`,
    retentionPercentage: 5,
    retentionLimit: Math.round(seed.contractValue * 0.025),
    paymentTerms: 30,
    siteAddress: `${seed.town}, ${seed.postcode}`,
    siteAddressLine1: `${seed.name.split('-')[0].trim()}`,
    siteTown: seed.town,
    siteCounty: 'Greater London',
    sitePostcode: seed.postcode,
    latitude: seed.lat,
    longitude: seed.lng,
    projectManager: 'John Smith',
    quantitySurveyor: 'Sarah Walsh',
    // Every demo project gets the same in-house team so the Team panel on
    // the project overview is never empty: PD + QS + SM + Foreman.
    // (`userId` is the demo user / John Smith — Commercial Director.)
    assignedStaffIds: [userId, 'demo-team-004', 'demo-team-003', 'demo-team-002', 'demo-team-006'],
    mainContractorContactIds: [],
    assignedSupervisorIds: [],
    archiveStatus: 'active',
    canRestore: true,
    notes: '',
    createdAt: created,
    updatedAt: isoDaysAgo(Math.max(1, Math.floor(seed.startedDaysAgo / 4))),
    createdBy: userId,
  };
}

// ─── Demo contract items (RFIs, variations) ───────────────────────────────────

interface ContractItemSeed {
  id: string;
  projectId: string;
  type: ContractItem['type'];
  reference: string;
  title: string;
  description: string;
  status: ContractItem['status'];
  priority: ContractItem['priority'];
  raisedDaysAgo: number;
  dueInDays?: number;
  value?: number;
}

const CONTRACT_ITEM_SEEDS: ContractItemSeed[] = [
  // RFIs we've raised to the main contractor
  {
    id: 'demo-rfi-001',
    projectId: 'demo-proj-001',
    type: 'rfi',
    reference: 'RFI-018-004',
    title: 'Confirm wall finish to lift lobby',
    description: 'Drawing pack lists two finishes for the lift-lobby walls, please confirm which spec applies before we start fixing plasterboard.',
    status: 'open',
    priority: 'medium',
    raisedDaysAgo: 4,
    dueInDays: 3,
  },
  {
    id: 'demo-rfi-002',
    projectId: 'demo-proj-002',
    type: 'rfi',
    reference: 'RFI-022-011',
    title: 'Riser cut-out coordination, Block A level 3',
    description: 'We have a clash with the structural beam at level 3, need confirmation of revised cut-out positions before second-fix.',
    status: 'open',
    priority: 'high',
    raisedDaysAgo: 2,
    dueInDays: 1,
  },
  // Variations we've claimed against our subcontract
  {
    id: 'demo-var-001',
    projectId: 'demo-proj-001',
    type: 'variation',
    reference: 'VO-018-007',
    title: 'Additional fire-rated plasterboard to riser walls',
    description: 'Brookford instructed FR-rated boards to all riser walls (not in tender). Net add £18,400 to our package.',
    status: 'pending-approval',
    priority: 'medium',
    raisedDaysAgo: 6,
    dueInDays: 5,
    value: 18_400,
  },
  {
    id: 'demo-var-002',
    projectId: 'demo-proj-002',
    type: 'variation',
    reference: 'VO-022-014',
    title: 'Out-of-hours working, riser remedial',
    description: 'Site instruction to complete riser remedials over weekend to maintain handover. Two crew, two shifts.',
    status: 'pending-approval',
    priority: 'low',
    raisedDaysAgo: 9,
    dueInDays: 11,
    value: 8_600,
  },
];

function buildContractItem(seed: ContractItemSeed, userId: string): ContractItem {
  const dateRaised = isoDaysAgo(seed.raisedDaysAgo);
  return {
    id: seed.id,
    projectId: seed.projectId,
    type: seed.type,
    reference: seed.reference,
    title: seed.title,
    description: seed.description,
    status: seed.status,
    priority: seed.priority,
    dateRaised,
    dueDate: seed.dueInDays !== undefined ? dateOnly(isoDaysAhead(seed.dueInDays)) : undefined,
    value: seed.value,
    raisedBy: userId,
    createdAt: dateRaised,
    updatedAt: dateRaised,
  };
}

// ─── Demo interim applications ────────────────────────────────────────────────

interface InterimSeed {
  id: string;
  projectId: string;
  appNumber: number;
  periodEndsDaysAgo: number;
  thisApp: number;
  cumulative: number;
  status: InterimApplication['status'];
}

const INTERIM_SEEDS: InterimSeed[] = [
  // Westwood Mews fit-out, 5 monthly apps, ~£980k package
  { id: 'demo-int-001', projectId: 'demo-proj-001', appNumber: 5, periodEndsDaysAgo: 4,   thisApp: 110_000, cumulative: 540_000, status: 'submitted' },
  { id: 'demo-int-002', projectId: 'demo-proj-001', appNumber: 4, periodEndsDaysAgo: 35,  thisApp: 125_000, cumulative: 430_000, status: 'paid' },
  { id: 'demo-int-003', projectId: 'demo-proj-001', appNumber: 3, periodEndsDaysAgo: 65,  thisApp: 140_000, cumulative: 305_000, status: 'paid' },
  // Aldgate Block A fit-out, 3 monthly apps, ~£2.4M package
  { id: 'demo-int-004', projectId: 'demo-proj-002', appNumber: 3, periodEndsDaysAgo: 8,   thisApp: 380_000, cumulative: 1_010_000, status: 'certified' },
  { id: 'demo-int-005', projectId: 'demo-proj-002', appNumber: 2, periodEndsDaysAgo: 38,  thisApp: 360_000, cumulative: 630_000,   status: 'paid' },
  // Marylebone strip-out, final account, ~£290k package
  { id: 'demo-int-006', projectId: 'demo-proj-006', appNumber: 11, periodEndsDaysAgo: 22, thisApp: 14_000,  cumulative: 282_000,   status: 'submitted' },
];

function buildInterim(seed: InterimSeed): InterimApplication {
  const periodEnd = isoDaysAgo(seed.periodEndsDaysAgo);
  const periodStart = isoDaysAgo(seed.periodEndsDaysAgo + 30);
  const submitted = isoDaysAgo(Math.max(seed.periodEndsDaysAgo - 2, 0));
  const dueIn = 30 - seed.periodEndsDaysAgo;
  const paymentDue = isoDaysAhead(dueIn);
  const retention = Math.round(seed.cumulative * 0.05);
  return {
    id: seed.id,
    projectId: seed.projectId,
    applicationNumber: seed.appNumber,
    periodStart,
    periodEnd,
    status: seed.status,
    breakdown: {
      previouslyAgreed: seed.cumulative - seed.thisApp,
      thisApplication: seed.thisApp,
      cumulativeToDate: seed.cumulative,
    },
    materials: [],
    variations: [],
    retention: {
      percentage: 5,
      currentRelease: 0,
      cumulativeRetained: retention,
      cumulativeReleased: 0,
      balance: retention,
    },
    summary: {
      grossValuation: seed.cumulative,
      lessRetention: retention,
      netValuation: seed.cumulative - retention,
      lessPreviouslyCertified: seed.cumulative - seed.thisApp,
      amountDue: seed.thisApp,
      vat: Math.round(seed.thisApp * 0.2),
      totalIncVat: Math.round(seed.thisApp * 1.2),
    },
    dateSubmitted: submitted,
    dateCertified: seed.status === 'certified' || seed.status === 'paid' ? submitted : undefined,
    datePaid: seed.status === 'paid' ? isoDaysAgo(Math.max(seed.periodEndsDaysAgo - 14, 0)) : undefined,
    dueDateFinalPayment: paymentDue,
    createdAt: submitted,
    updatedAt: submitted,
    // Snake-case mirrors used by some legacy reads (dashboard metrics):
    ...({
      project_id: seed.projectId,
      application_number: seed.appNumber,
      cumulative_total: seed.cumulative,
      amount_paid: seed.status === 'paid' ? seed.cumulative : 0,
      valuation_date: periodEnd,
      payment_due_date: paymentDue,
      created_at: submitted,
    } as any),
  };
}

// ─── Demo team members ────────────────────────────────────────────────────────
//
// Every project lists these as assignedStaff so the project overview Team
// panel always has a crew, instead of the empty-state. The demo user (John
// Smith) sits at the top — the rest are colleagues at Smith Build Ltd.

interface TeamSeed {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
}

const TEAM_SEEDS: TeamSeed[] = [
  { id: 'demo-team-002', name: 'Mark Davies',  email: 'mark.davies@smithbuild.demo',   jobTitle: 'Site Manager' },
  { id: 'demo-team-003', name: 'Sarah Walsh',  email: 'sarah.walsh@smithbuild.demo',   jobTitle: 'Quantity Surveyor' },
  { id: 'demo-team-004', name: 'Tom Reilly',   email: 'tom.reilly@smithbuild.demo',    jobTitle: 'Project Director' },
  { id: 'demo-team-005', name: 'Amelia Jones', email: 'amelia.jones@smithbuild.demo',  jobTitle: 'Document Controller' },
  { id: 'demo-team-006', name: 'David Khan',   email: 'david.khan@smithbuild.demo',    jobTitle: 'Foreman' },
];

function buildTeamMember(seed: TeamSeed, companyId: string): User {
  return {
    id: seed.id,
    email: seed.email,
    name: seed.name,
    firstName: seed.name.split(' ')[0],
    lastName: seed.name.split(' ').slice(1).join(' '),
    displayName: seed.name,
    // Demo users represent a prospective *customer* exploring the
    // platform from the customer side — they belong to the demo
    // workspace (type='organisation'), not to CG ops. Hardcoding
    // 'staff' here was leaking the CG-side dashboard into the demo
    // experience post role-migration (legacy 'staff' was being
    // recognised as 'internal' by isStaff() / isAdmin()).
    role: 'member',
    position: seed.jobTitle,
    jobTitle: seed.jobTitle,
    department: 'Operations',
    companyId,
    company_id: companyId,
    status: 'active',
  } as unknown as User;
}

// ─── Demo tasks ───────────────────────────────────────────────────────────────

interface TaskSeed {
  id: string;
  projectId: string;
  title: string;
  description: string;
  type: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  dueInDays: number;          // negative = overdue, positive = upcoming
  completedDaysAgo?: number;
}

const TASK_SEEDS: TaskSeed[] = [
  // Westwood Mews fit-out, on site
  { id: 'demo-task-001', projectId: 'demo-proj-001', title: 'Submit fit-out RAMS rev B',                  description: 'Brookford requested updated method statement covering second-fix M&E coordination.',                                       type: 'submission', priority: 'high',     status: 'pending',     dueInDays: 2 },
  { id: 'demo-task-002', projectId: 'demo-proj-001', title: 'Submit interim app #5',                      description: 'Net £110k for the period ending last week. Pack ready, signature needed before lodging.',                                   type: 'submission', priority: 'medium',   status: 'pending',     dueInDays: 5 },
  { id: 'demo-task-003', projectId: 'demo-proj-001', title: 'Lodge variation, fire-rated boards',        description: 'VO-018-007 paperwork ready, £18,400 net. Submit to Brookford QS.',                                                       type: 'submission', priority: 'medium',   status: 'in_progress', dueInDays: 4 },
  { id: 'demo-task-004', projectId: 'demo-proj-001', title: 'Issue lift-lobby finish samples',            description: 'Three boards prepared for client/architect sign-off via Brookford.',                                                       type: 'submission', priority: 'low',      status: 'completed',   dueInDays: -3, completedDaysAgo: 4 },
  // Aldgate Block A fit-out
  { id: 'demo-task-005', projectId: 'demo-proj-002', title: 'Chase RFI-022-011 (riser cut-out)',          description: 'No response from Holden & Marsh PM since Tuesday, cut-out blocking second-fix.',                                          type: 'chase',      priority: 'critical', status: 'pending',     dueInDays: 1 },
  { id: 'demo-task-006', projectId: 'demo-proj-002', title: 'Lodge variation, out-of-hours working',     description: 'Weekend remedials VO-022-014, £8,600. Submit timesheets + claim to MC.',                                                  type: 'submission', priority: 'low',      status: 'pending',     dueInDays: 11 },
  { id: 'demo-task-007', projectId: 'demo-proj-002', title: 'Joint progress walk with Holden & Marsh',    description: 'Fortnightly programme review on site, take photos for our progress report.',                                              type: 'meeting',    priority: 'medium',   status: 'in_progress', dueInDays: 7 },
  { id: 'demo-task-008', projectId: 'demo-proj-002', title: 'Counter-sign certified app #3',              description: 'Holden & Marsh QS certified £380k net, countersign and return for payment release.',                                       type: 'submission', priority: 'high',     status: 'pending',     dueInDays: -1 },
  // Battersea, our tender is in with Linbrook (their "client review")
  { id: 'demo-task-009', projectId: 'demo-proj-003', title: 'Hold post-tender meeting with Linbrook',     description: 'Linbrook QS wants to walk through our submission, Wednesday 11am at their office.',                                       type: 'meeting',    priority: 'critical', status: 'pending',     dueInDays: 4 },
  { id: 'demo-task-010', projectId: 'demo-proj-003', title: 'Issue cost plan rev D',                      description: 'Linbrook came back with three queries, adjust rates and reissue with covering letter.',                                    type: 'submission', priority: 'high',     status: 'pending',     dueInDays: 6 },
  // Hampstead, refurb tender with Pemberton
  { id: 'demo-task-011', projectId: 'demo-proj-004', title: 'Reissue strip-out programme to Pemberton',   description: 'Pemberton flagged a clash with their heritage works, adjust our sequence and resend.',                                     type: 'submission', priority: 'high',     status: 'pending',     dueInDays: 3 },
  // Kingsland Yard, we are pricing
  { id: 'demo-task-012', projectId: 'demo-proj-005', title: 'Return Kingsland Yard tender',               description: 'Thornhill issued tender pack, pricing returnable in 8 days. BOQ priced, awaiting prelims.',                                type: 'submission', priority: 'medium',   status: 'in_progress', dueInDays: 8 },
  // Marylebone strip-out, final account
  { id: 'demo-task-013', projectId: 'demo-proj-006', title: 'Agree final account with Brookford',         description: 'Outstanding £14k disputed, meeting Tuesday to settle.',                                                                  type: 'meeting',    priority: 'high',     status: 'in_progress', dueInDays: 14 },
  { id: 'demo-task-014', projectId: 'demo-proj-006', title: 'Submit retention release request',           description: 'Practical completion certified, request 50% retention release from Brookford.',                                              type: 'submission', priority: 'medium',   status: 'completed',   dueInDays: -7, completedDaysAgo: 5 },
  // Greenwich Quay, pre-contract
  { id: 'demo-task-015', projectId: 'demo-proj-007', title: 'Execute subcontract with Holden & Marsh',   description: 'Subcontract documents issued, initial each page and return signed before mobilisation.',                                     type: 'submission', priority: 'high',     status: 'pending',     dueInDays: 5 },
];

function buildTask(seed: TaskSeed, project: Project, companyId: string, userId: string, userName: string): Task {
  const created = new Date(Date.now() - 4 * ms);
  const updated = new Date();
  const due = seed.dueInDays !== undefined ? new Date(Date.now() + seed.dueInDays * ms) : undefined;
  const completed = seed.completedDaysAgo !== undefined ? new Date(Date.now() - seed.completedDaysAgo * ms) : undefined;
  return {
    id: seed.id,
    project_id: project.id,
    project_name: project.name,
    client_company_id: companyId,
    client_name: 'Smith Build Ltd',
    title: seed.title,
    description: seed.description,
    type: seed.type as any,
    priority: seed.priority,
    status: seed.status as any,
    source: 'manual' as any,
    created_by: userId,
    created_by_name: userName,
    assigned_to: userId,
    assigned_to_name: userName,
    created_at: created,
    updated_at: updated,
    due_date: due,
    completed_date: completed,
    visible_to_client: true,
    client_can_edit: true,
  } as Task;
}

// ─── Demo activity feed ───────────────────────────────────────────────────────

interface ActivitySeed {
  id: string;
  type: Activity['type'];
  title: string;
  description: string;
  projectId: string;
  daysAgo: number;
  hoursAgo?: number;
  emailSubject?: string;
  emailFrom?: string;
}

const ACTIVITY_SEEDS: ActivitySeed[] = [
  // Headlines, from a subby's perspective: things WE did or things the
  // main contractor did to OUR package.
  { id: 'demo-act-001', type: 'project_update',      title: 'Battersea Wharf Cat A, tender returned to Linbrook', description: 'Our tender for the Cat A fit-out package issued, awaiting Linbrook decision.',  projectId: 'demo-proj-003', daysAgo: 0, hoursAgo: 3 },
  { id: 'demo-act-002', type: 'interim_application', title: 'Application #5 lodged, Westwood Mews',               description: 'Net £110,000 submitted to Brookford QS for the period ending last week.',         projectId: 'demo-proj-001', daysAgo: 0, hoursAgo: 6 },
  { id: 'demo-act-003', type: 'contract_item',       title: 'Variation lodged, Aldgate Block A',                  description: 'Out-of-hours working claim for riser remedials, £8,600 net.',                     projectId: 'demo-proj-002', daysAgo: 1 },
  { id: 'demo-act-004', type: 'project_update',      title: 'Hampstead refurb, programme reissued',               description: 'Pemberton flagged a clash with the heritage works, revised strip-out sequence sent.', projectId: 'demo-proj-004', daysAgo: 1 },
  { id: 'demo-act-005', type: 'status_update',       title: 'Marylebone strip-out, final account opened',         description: 'Brookford confirmed PC; we have moved the package into final account.',           projectId: 'demo-proj-006', daysAgo: 2 },
  { id: 'demo-act-006', type: 'interim_application', title: 'Application #3 certified, Aldgate Block A',          description: 'Holden & Marsh certified £380,000 net of retention. Payment due within 30 days.', projectId: 'demo-proj-002', daysAgo: 3 },

  // Notes, site-level + commercial admin from our side
  { id: 'demo-note-001', type: 'note', title: 'First-fix complete on level 4',                  description: 'All M&E first-fix signed off. Commencing plasterboard Monday.',                                   projectId: 'demo-proj-001', daysAgo: 0, hoursAgo: 5 },
  { id: 'demo-note-002', type: 'note', title: 'Show-home finishes signed off',                  description: 'Brookford confirmed Option B for the lift-lobby finishes. Ordering materials.',                  projectId: 'demo-proj-001', daysAgo: 4 },
  { id: 'demo-note-003', type: 'note', title: 'Riser cut-out blocked, chasing RFI',            description: 'Cut-out clash blocking second-fix on level 3. Logged RFI-022-011 with Holden & Marsh.',          projectId: 'demo-proj-002', daysAgo: 1 },
  { id: 'demo-note-004', type: 'note', title: 'Post-tender meeting on Wednesday',                description: 'Linbrook QS wants to walk through our submission, bringing the project lead.',                  projectId: 'demo-proj-003', daysAgo: 2 },
  { id: 'demo-note-005', type: 'note', title: 'Asbestos survey received',                       description: 'Pemberton sent the R&D survey, three positives flagged for our strip-out programme.',           projectId: 'demo-proj-004', daysAgo: 3 },
  { id: 'demo-note-006', type: 'note', title: 'Tender priced, awaiting prelims confirmation', description: 'BOQ priced, waiting on programme + prelims from PM before we issue.',                            projectId: 'demo-proj-005', daysAgo: 5 },

  // Emails, incoming from the main contractor's commercial teams
  { id: 'demo-email-001', type: 'email', title: 'Re: Lift-lobby finish samples',         description: 'Sample boards received, Option B confirmed by client. Proceed on that basis., James (Brookford)', projectId: 'demo-proj-001', daysAgo: 0, hoursAgo: 8, emailSubject: 'Re: Lift-lobby finish samples',                  emailFrom: 'james.holt@brookford.co.uk' },
  { id: 'demo-email-002', type: 'email', title: 'Application #5, certificate attached',  description: 'Net £110,000 certified. Payment expected within 28 days per subcontract.',                       projectId: 'demo-proj-001', daysAgo: 0, hoursAgo: 7, emailSubject: 'Application #5, certificate attached',           emailFrom: 'qs@brookford.co.uk' },
  { id: 'demo-email-003', type: 'email', title: 'Riser cut-out, drawings for clarification', description: 'Revised cut-out positions attached. Engineer sign-off in progress, will revert tomorrow.',         projectId: 'demo-proj-002', daysAgo: 1,                emailSubject: 'Riser cut-out, drawings for clarification',     emailFrom: 'pm@holden-marsh.co.uk' },
  { id: 'demo-email-004', type: 'email', title: 'Post-tender meeting, Wednesday 11am',    description: 'Confirming Wednesday 11am at our office. Bring your priced BOQ, we have three queries to walk through.', projectId: 'demo-proj-003', daysAgo: 2,                emailSubject: 'Post-tender meeting, Wednesday 11am',           emailFrom: 'qs@linbrookbuild.co.uk' },
  { id: 'demo-email-005', type: 'email', title: 'R&D asbestos survey, for your programme', description: 'Asbestos R&D survey attached. Three positives, plan strip-out around them.',                       projectId: 'demo-proj-004', daysAgo: 3,                emailSubject: 'R&D asbestos survey, for your programme',       emailFrom: 'projects@pembertonheritage.co.uk' },
  { id: 'demo-email-006', type: 'email', title: 'Final account, outstanding items',        description: 'Final account spreadsheet attached. £14k disputed against your VO-011-003. Meeting Tues to settle.', projectId: 'demo-proj-006', daysAgo: 4,                emailSubject: 'Final account, outstanding items',              emailFrom: 'finance@brookford.co.uk' },
  { id: 'demo-email-007', type: 'email', title: 'Subcontract, execution copy',             description: 'JCT subcontract execution copy attached. Please initial each page and return signed before mobilisation.', projectId: 'demo-proj-007', daysAgo: 1,                emailSubject: 'Subcontract, execution copy',                   emailFrom: 'legal@holden-marsh.co.uk' },
];

function buildActivity(seed: ActivitySeed, userId: string, userName: string, userEmail: string, projectName: string): Activity {
  const created = seed.hoursAgo !== undefined
    ? new Date(Date.now() - seed.daysAgo * ms - seed.hoursAgo * 3_600_000).toISOString()
    : isoDaysAgo(seed.daysAgo);
  const isEmail = seed.type === 'email';
  return {
    id: seed.id,
    type: seed.type,
    related_to_type: 'project',
    related_to_id: seed.projectId,
    related_to_name: projectName,
    title: seed.title,
    description: seed.description,
    created_by_id: userId,
    created_by_name: userName,
    created_by_email: userEmail,
    created_at: created,
    // Required by Activity filter helpers, must always be defined arrays/booleans.
    mentions: [],
    is_pinned: false,
    requires_follow_up: false,
    // Email-specific decoration so the Emails widget shows subject + from.
    email_subject: isEmail ? seed.emailSubject : undefined,
    email_to: isEmail ? [userEmail] : undefined,
    email_sent_at: isEmail ? created : undefined,
    // Mirror project_id at the top level, backend column is `project_id`,
    // and `getProjectActivities` filters on `related_to_id` (which is fine),
    // but other readers expect the flat field.
    ...({ project_id: seed.projectId, email_from: seed.emailFrom } as any),
  } as Activity;
}

// ─── Builder ──────────────────────────────────────────────────────────────────

export function buildDemoDatabase(emptyDb: Database, user: User): Database {
  const companyId = (user as any).companyId || (user as any).company_id || DEMO_COMPANY_ID;
  const userId = user.id;
  const userName  = (user as any).name || 'Demo User';
  const userEmail = (user as any).email || 'demo@quantis.app';

  const company = { ...buildCompany(companyId), id: companyId };
  const projects = PROJECT_SEEDS.map(seed => buildProject(seed, companyId, userId));
  const contractItems = CONTRACT_ITEM_SEEDS.map(seed => buildContractItem(seed, userId));
  const interimApps = INTERIM_SEEDS.map(buildInterim);
  const projectById = (id: string) => projects.find(p => p.id === id);
  const tasks = TASK_SEEDS
    .map(seed => {
      const proj = projectById(seed.projectId);
      return proj ? buildTask(seed, proj, companyId, userId, userName) : null;
    })
    .filter((t): t is Task => t !== null);

  const projectName = (id: string) => projectById(id)?.name || 'Project';
  const activities = ACTIVITY_SEEDS.map(seed =>
    buildActivity(seed, userId, userName, userEmail, projectName(seed.projectId))
  );

  const teamMembers = TEAM_SEEDS.map(seed => buildTeamMember(seed, companyId));

  return {
    ...emptyDb,
    companies: { [companyId]: company },
    users: {
      [userId]: user,
      ...Object.fromEntries(teamMembers.map(m => [m.id, m])),
    },
    projects: Object.fromEntries(projects.map(p => [p.id, p])),
    contractItems: Object.fromEntries(contractItems.map(c => [c.id, c])),
    interimApplications: Object.fromEntries(interimApps.map(a => [a.id, a])),
    tasks: Object.fromEntries(tasks.map(t => [t.id, t])) as any,
    activities: Object.fromEntries(activities.map(a => [a.id, a])),
    lastUpdated: new Date().toISOString(),
  };
}

// ─── Demo BOQ items ──────────────────────────────────────────────────────────
//
// Per-project bill-of-quantities. A real subcontractor's pricing build-up
// for a fit-out / refurb package: prelims (bill 1), partitions (bill 2),
// ceilings (bill 3), floor finishes (bill 4), decoration (bill 5), doors
// & ironmongery (bill 6). Quantities scale with the package contract value.
//
// Returned shape matches `BOQItem` from src/types/boq.ts — the fields we
// don't populate (resourceBreakdown, dimensions, etc.) are optional.

interface BOQTemplateItem {
  desc: string;
  unit: 'm2' | 'm' | 'nr' | 'item';
  qtyPerKValue: number;   // qty per £1k of contract value (rounded by formula)
  rate: number;
  // Resource split (must sum to `rate`). Drives the system's built-in
  // Labour / Materials / Plant summary tabs that always sit at the end of
  // the BOQ tab strip.
  labourRate: number;
  materialRate: number;
  plantRate: number;
}

// Two seed bills only: Preliminaries + Measured Works. The BOQ tab strip
// auto-appends three resource-rollup tabs (Labour, Materials, Plant) that
// derive their totals from the resource splits below — so the user ends
// up seeing the full 5-tab structure: Preliminaries | Measured Works |
// Labour | Materials | Plant. We don't seed Labour/Plant/Material as
// separate bills because they would duplicate the system tabs.
const BOQ_TEMPLATE: Array<{ bill: number; title: string; items: BOQTemplateItem[] }> = [
  {
    bill: 1, title: 'Preliminaries', items: [
      { desc: 'Site set-up, welfare and security for the duration of the works', unit: 'item', qtyPerKValue: 0, rate: 0, labourRate: 0, materialRate: 0, plantRate: 0 },
      { desc: 'Site management, non-working PM and supervisor',                   unit: 'item', qtyPerKValue: 0, rate: 0, labourRate: 0, materialRate: 0, plantRate: 0 },
      { desc: 'Insurances, bonds and statutory contributions',                    unit: 'item', qtyPerKValue: 0, rate: 0, labourRate: 0, materialRate: 0, plantRate: 0 },
    ],
  },
  {
    bill: 2, title: 'Measured Works', items: [
      // Plasterboard partitions
      { desc: 'New 70mm metal stud partition with 2-layer board both sides, FR-rated', unit: 'm2', qtyPerKValue: 0.42,  rate: 64,   labourRate: 28,   materialRate: 32,   plantRate: 4    },
      { desc: 'Form openings in partitions for door sets',                              unit: 'nr', qtyPerKValue: 0.012, rate: 145,  labourRate: 95,   materialRate: 35,   plantRate: 15   },
      { desc: 'Cill / pelmet bulkheads to perimeter at suspended ceiling level',        unit: 'm',  qtyPerKValue: 0.18,  rate: 38,   labourRate: 18,   materialRate: 18,   plantRate: 2    },
      // Suspended ceilings
      { desc: 'Lay-in metal grid suspended ceiling with 600x600 mineral fibre tiles',   unit: 'm2', qtyPerKValue: 0.55,  rate: 42,   labourRate: 18,   materialRate: 22,   plantRate: 2    },
      { desc: 'Plasterboard MF ceiling system to perimeter strip',                       unit: 'm2', qtyPerKValue: 0.08,  rate: 78,   labourRate: 38,   materialRate: 36,   plantRate: 4    },
      // Floor finishes
      { desc: 'Self-levelling latex screed to receive finishes',                          unit: 'm2', qtyPerKValue: 0.62,  rate: 14,   labourRate: 6,    materialRate: 7,    plantRate: 1    },
      { desc: 'Heavy-contract loop pile carpet tile, supply & install',                   unit: 'm2', qtyPerKValue: 0.40,  rate: 38,   labourRate: 12,   materialRate: 25,   plantRate: 1    },
      { desc: 'LVT planks to circulation and lift lobby areas',                            unit: 'm2', qtyPerKValue: 0.10,  rate: 56,   labourRate: 16,   materialRate: 38,   plantRate: 2    },
      { desc: 'Stainless-steel transition strip between finishes',                         unit: 'm',  qtyPerKValue: 0.04,  rate: 22,   labourRate: 8,    materialRate: 13,   plantRate: 1    },
      // Decoration
      { desc: 'Mist coat plus 2 finish coats of contract emulsion to walls and ceilings',  unit: 'm2', qtyPerKValue: 1.85,  rate: 8.5,  labourRate: 5.5,  materialRate: 2.8,  plantRate: 0.2  },
      { desc: 'Eggshell paint to door frames and skirtings',                                unit: 'm',  qtyPerKValue: 0.35,  rate: 6.2,  labourRate: 4,    materialRate: 2,    plantRate: 0.2  },
      // Doors and ironmongery
      { desc: 'Solid-core flush door, FD30 rated, factory-finished veneer',                unit: 'nr', qtyPerKValue: 0.012, rate: 285,  labourRate: 65,   materialRate: 215,  plantRate: 5    },
      { desc: 'Ironmongery set, handles, locks, hinges, closers',                           unit: 'nr', qtyPerKValue: 0.012, rate: 165,  labourRate: 35,   materialRate: 128,  plantRate: 2    },
      { desc: 'Single-leaf glazed feature doors to meeting rooms',                          unit: 'nr', qtyPerKValue: 0.003, rate: 980,  labourRate: 145,  materialRate: 820,  plantRate: 15   },
    ],
  },
];

interface DemoBOQResource {
  description: string;
  category: 'labour' | 'material' | 'plant';
  constant: number;        // qty multiplier per item-unit
  unit: string;
  unit_rate: number;       // £ per resource-unit
  waste_pct?: number;
}

interface DemoBOQItem {
  id: string;
  itemNumber: string;
  itemType: 'bill-header' | 'item';
  level: 1 | 4;
  parentId?: string;
  billId: string;          // points at the bill row (matches BOQBuilder filter)
  billNumber?: string;
  billTitle?: string;
  description: string;
  unit: string;
  quantity: number;
  rate: number;
  total: number;
  // Resource split — drives the Labour / Materials / Plant summary tabs.
  // Both forms are populated: flat rates for the totals/footer aggregator
  // (`measuredDisplayItems.reduce(... item.labourRate ...)`) and the
  // resources array for `BOQResourceSummary`'s row-level aggregation.
  labourRate?: number;
  materialRate?: number;
  plantRate?: number;
  resources?: DemoBOQResource[];
  sortOrder: number;
  status: 'priced';
  isProvisional: boolean;
  isLumpSum: boolean;
  isPrimeCost: boolean;
  isVariation: boolean;
}

/**
 * Bills (the BOQ "tabs" — one per top-level bill heading) derived from the
 * template. Used by `useProjectBills` so the BOQ tab strip isn't empty.
 */
export function getDemoBills(projectId: string): Array<{
  id: string;
  project_id: string;
  bill_no: string;
  bill_title: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}> {
  const seed = PROJECT_SEEDS.find(p => p.id === projectId);
  if (!seed) return [];
  const now = new Date().toISOString();
  return BOQ_TEMPLATE.map((bill) => ({
    id: `${projectId}-bill-${bill.bill}`,
    project_id: projectId,
    bill_no: String(bill.bill),
    bill_title: bill.title,
    display_order: bill.bill,
    created_at: now,
    updated_at: now,
  }));
}

/**
 * Build a project-specific BOQ from the template. Quantities are scaled by
 * the project's contract value so a £2.4M package shows correspondingly
 * larger numbers than a £290k strip-out.
 */
export function getDemoBOQItems(projectId: string): DemoBOQItem[] {
  const seed = PROJECT_SEEDS.find(p => p.id === projectId);
  if (!seed) return [];

  const valueK = seed.contractValue / 1_000;          // contract value in £k
  // Allocate ~6% of value to prelims as a lump sum, the rest split via the
  // template quantities × rates. This keeps sums realistic to total.
  const prelimsLump = Math.round(seed.contractValue * 0.06);
  const prelimsAllocations = [0.55, 0.30, 0.15];      // 55/30/15 of prelims

  const items: DemoBOQItem[] = [];
  let sortOrder = 0;
  let itemSeq = 0;

  BOQ_TEMPLATE.forEach((bill) => {
    const billRowId = `${projectId}-bill-${bill.bill}`;
    items.push({
      id: billRowId,
      itemNumber: `${bill.bill}`,
      itemType: 'bill-header',
      level: 1,
      billId: billRowId,
      billNumber: String(bill.bill),
      billTitle: bill.title,
      description: `Bill No. ${bill.bill}, ${bill.title}`,
      unit: '',
      quantity: 0,
      rate: 0,
      total: 0,
      sortOrder: sortOrder++,
      status: 'priced',
      isProvisional: false,
      isLumpSum: false,
      isPrimeCost: false,
      isVariation: false,
    });

    bill.items.forEach((tplItem, idxInBill) => {
      itemSeq++;
      let quantity: number;
      let rate: number;
      let labourRate: number;
      let materialRate: number;
      let plantRate: number;
      let isLump = false;

      if (bill.bill === 1) {
        // Prelims: distribute the lump-sum allocation across the 3 lines.
        // Treat as 100% labour for resource-rollup purposes (PM/welfare/etc.
        // are largely staff cost) so the Labour summary tab includes them.
        quantity = 1;
        rate = Math.round(prelimsLump * (prelimsAllocations[idxInBill] ?? 0));
        labourRate = rate;
        materialRate = 0;
        plantRate = 0;
        isLump = true;
      } else {
        quantity = Math.max(1, Math.round(tplItem.qtyPerKValue * valueK));
        rate = tplItem.rate;
        labourRate = tplItem.labourRate;
        materialRate = tplItem.materialRate;
        plantRate = tplItem.plantRate;
      }
      const total = Math.round(quantity * rate);

      // Synthesise a `resources` build-up so the Labour / Materials / Plant
      // summary tabs (BOQResourceSummary) have data to roll up. Validator
      // rules require labour and plant resources to be priced per hour, so
      // we express the constant as hours-per-item-unit at a notional
      // £38/hr skilled rate (labour) and £30/hr (plant). Materials keep
      // the item unit since they're typically priced per m² / m / nr.
      const LABOUR_HOURLY = 38;
      const PLANT_HOURLY  = 30;
      const resources: DemoBOQResource[] = [];
      if (labourRate > 0) {
        resources.push({
          description: 'Labour',
          category: 'labour',
          constant: Number((labourRate / LABOUR_HOURLY).toFixed(3)),
          unit: 'hr',
          unit_rate: LABOUR_HOURLY,
        });
      }
      if (materialRate > 0) {
        resources.push({
          description: 'Materials',
          category: 'material',
          constant: 1,
          unit: tplItem.unit,
          unit_rate: materialRate,
          waste_pct: 5,
        });
      }
      if (plantRate > 0) {
        resources.push({
          description: 'Plant and small tools',
          category: 'plant',
          constant: Number((plantRate / PLANT_HOURLY).toFixed(3)),
          unit: 'hr',
          unit_rate: PLANT_HOURLY,
        });
      }

      items.push({
        id: `${projectId}-item-${itemSeq}`,
        itemNumber: `${bill.bill}.${idxInBill + 1}`,
        itemType: 'item',
        level: 4,
        parentId: billRowId,
        billId: billRowId,
        billNumber: String(bill.bill),
        description: tplItem.desc,
        unit: tplItem.unit,
        quantity,
        rate,
        total,
        labourRate,
        materialRate,
        plantRate,
        resources,
        sortOrder: sortOrder++,
        status: 'priced',
        isProvisional: false,
        isLumpSum: isLump,
        isPrimeCost: false,
        isVariation: false,
      });
    });
  });

  return items;
}

/**
 * Patch a User with the demo company id so AuthContext's company lookup
 * resolves to the fixture company. Returns a new user object.
 */
export function patchDemoUserCompany(user: User): User {
  // Always normalise the demo user's role to 'member'. Cached sessions
  // from before the role-migration may still carry role='staff', which
  // would route the demo into the CG ops dashboard — not what a
  // prospective customer should see when evaluating Quantis. Forcing
  // 'member' here on every restore keeps the demo experience faithful
  // regardless of what's been persisted in localStorage.
  const normalisedRole = ((user as any).role && (user as any).role !== 'staff' && (user as any).role !== 'admin')
    ? (user as any).role
    : 'member';

  const existingCompanyId = (user as any).companyId || (user as any).company_id;
  const baseUser = existingCompanyId
    ? user
    : ({
        ...user,
        companyId: DEMO_COMPANY_ID,
        company_id: DEMO_COMPANY_ID,
      } as User);

  return { ...baseUser, role: normalisedRole } as User;
}
